<template>
  <modal 
    :value="showModal" 
    title="Merge Schools"
    @input="updateShowModal"
    size="lg"
    :backdrop="false"
  >
    <div v-if="currentStep === 'selection'" class="merge-schools-form">
      <div v-if="errorMessage" class="alert alert-danger">
        <i class="fa fa-exclamation-circle"></i>
        {{ errorMessage }}
      </div>

      <div class="form-group">
        <label class="control-label">
          <strong>School to Keep</strong>
        </label>
        <p class="help-block">
          All users will be moved to this school
        </p>
        <select 
          v-model="targetSchoolUuid" 
          class="form-control"
          @change="clearError"
        >
          <option value="">-- Select School to Keep --</option>
          <option 
            v-for="school in availableSchools" 
            :key="school.uuid"
            :value="school.uuid"
          >
            {{ school.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="control-label">
          <strong>School to Remove</strong>
        </label>
        <p class="help-block">
          This school will be deleted after users are moved
        </p>
        <select 
          v-model="sourceSchoolUuid" 
          class="form-control"
          @change="clearError"
        >
          <option value="">-- Select School to Remove --</option>
          <option 
            v-for="school in availableSchools" 
            :key="school.uuid"
            :value="school.uuid"
            :disabled="school.uuid === targetSchoolUuid"
          >
            {{ school.name }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="currentStep === 'confirmation'" class="merge-schools-confirmation">
      <div class="alert alert-warning">
        <h4><i class="fa fa-warning"></i> Confirm School Merge</h4>
        <p><strong>This action cannot be undone.</strong></p>
      </div>

      <div class="merge-summary">
        <table class="table table-bordered">
          <tbody>
            <tr>
              <td><strong>School to Keep:</strong></td>
              <td>{{ targetSchool && targetSchool.name }}</td>
            </tr>
            <tr>
              <td><strong>School to Remove:</strong></td>
              <td>{{ sourceSchool && sourceSchool.name }}</td>
            </tr>
            <tr class="info">
              <td><strong>Users to Move:</strong></td>
              <td>{{ userCount }} user(s)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        <strong>{{ userCount }}</strong> user(s) will be moved from 
        <strong>{{ sourceSchool && sourceSchool.name }}</strong> to 
        <strong>{{ targetSchool && targetSchool.name }}</strong>.
      </p>
      <p>
        After the merge, <strong>{{ sourceSchool && sourceSchool.name }}</strong> will be deleted.
      </p>
    </div>

    <div v-if="currentStep === 'processing'" class="merge-schools-processing">
      <div class="alert alert-info text-center">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p class="lead">Merging schools...</p>
        <p>Please wait while we move users and delete the source school.</p>
      </div>
    </div>

    <div v-if="currentStep === 'success'" class="merge-schools-success">
      <div class="alert alert-success">
        <h4><i class="fa fa-check-circle"></i> Schools Merged Successfully</h4>
        <p>
          {{ userCount }} user(s) have been moved from 
          <strong>{{ sourceSchool && sourceSchool.name }}</strong> to 
          <strong>{{ targetSchool && targetSchool.name }}</strong>.
        </p>
        <p v-if="schoolDeleted">
          The school <strong>{{ sourceSchool && sourceSchool.name }}</strong> has been deleted.
        </p>
        <p v-else class="text-warning">
          <i class="fa fa-warning"></i>
          The school <strong>{{ sourceSchool && sourceSchool.name }}</strong> could not be deleted automatically.
        </p>
        
        <div v-if="removalErrors && removalErrors.length > 0" class="alert alert-warning" style="margin-top: 15px;">
          <h5><i class="fa fa-exclamation-triangle"></i> Removal Issues:</h5>
          <ul class="mb-0">
            <li v-for="error in removalErrors" :key="error">{{ error }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="currentStep === 'error'" class="merge-schools-error">
      <div class="alert alert-danger">
        <h4><i class="fa fa-exclamation-circle"></i> Merge Failed</h4>
        <p>{{ errorMessage }}</p>
      </div>
    </div>

    <div slot="footer">
      <button 
        v-if="currentStep === 'selection'" 
        type="button" 
        class="btn btn-default" 
        @click="onCancel"
      >
        Cancel
      </button>
      <button 
        v-if="currentStep === 'selection'" 
        type="button" 
        class="btn btn-primary" 
        @click="showConfirmation"
        :disabled="!canProceed"
      >
        Next
      </button>

      <button 
        v-if="currentStep === 'confirmation'" 
        type="button" 
        class="btn btn-default" 
        @click="backToSelection"
      >
        Back
      </button>
      <button 
        v-if="currentStep === 'confirmation'" 
        type="button" 
        class="btn btn-danger" 
        @click="executeMerge"
      >
        <i class="fa fa-compress"></i>
        Confirm Merge
      </button>

      <button 
        v-if="currentStep === 'success' || currentStep === 'error'" 
        type="button" 
        class="btn btn-primary" 
        @click="closeModal"
      >
        Close
      </button>
    </div>
  </modal>
</template>

<script>
import { Modal } from 'uiv'
import { getUserCountForSchool, validateMergeSelection } from './merge-schools-utils'

export default {
  name: 'MergeSchoolsModal',
  components: {
    Modal
  },
  props: {
    externalOpen: {
      type: Boolean,
      default: false
    },
    schools: {
      type: Array,
      required: true
    },
    users: {
      type: Array,
      required: true
    },
    domainUuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentStep: 'selection',
      sourceSchoolUuid: '',
      targetSchoolUuid: '',
      errorMessage: '',
      userCount: 0,
      schoolDeleted: false,
      removalErrors: []
    }
  },
  computed: {
    showModal() {
      return this.externalOpen
    },
    availableSchools() {
      return this.schools || []
    },
    sourceSchool() {
      return this.availableSchools.find(s => s.uuid === this.sourceSchoolUuid)
    },
    targetSchool() {
      return this.availableSchools.find(s => s.uuid === this.targetSchoolUuid)
    },
    canProceed() {
      return this.sourceSchoolUuid && this.targetSchoolUuid && 
             this.sourceSchoolUuid !== this.targetSchoolUuid
    }
  },
  watch: {
    externalOpen(newVal) {
      if (newVal) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.currentStep = 'selection'
      this.sourceSchoolUuid = ''
      this.targetSchoolUuid = ''
      this.errorMessage = ''
      this.userCount = 0
      this.schoolDeleted = false
      this.removalErrors = []
    },
    clearError() {
      this.errorMessage = ''
    },
    showConfirmation() {
      const validation = validateMergeSelection(this.sourceSchool, this.targetSchool)
      
      if (!validation.isValid) {
        this.errorMessage = validation.errors.join('. ')
        return
      }

      this.userCount = getUserCountForSchool(this.sourceSchoolUuid, this.users)
      this.currentStep = 'confirmation'
    },
    backToSelection() {
      this.currentStep = 'selection'
      this.errorMessage = ''
    },
    async executeMerge() {
      this.currentStep = 'processing'
      this.errorMessage = ''

      try {
        const response = await this.$angularWorld.adminApi.mergeSchools(
          this.sourceSchoolUuid, 
          this.targetSchoolUuid,
          this.domainUuid
        )
        
        this.schoolDeleted = response.deleted || false
        this.removalErrors = response.removal_errors || []
        
        this.currentStep = 'success'
        this.$emit('merge-success', {
          sourceSchool: this.sourceSchool,
          targetSchool: this.targetSchool,
          userCount: this.userCount,
          schoolDeleted: this.schoolDeleted,
          removalErrors: this.removalErrors
        })
      } catch (error) {
        console.error('Error merging schools:', error)
        this.errorMessage = typeof error === 'string' 
          ? error 
          : (error && error.message) || 'Failed to merge schools. Please try again.'
        this.currentStep = 'error'
        this.$emit('merge-error', error)
      }
    },
    closeModal() {
      this.$emit('update:externalOpen', false)
      if (this.currentStep === 'success') {
        this.$emit('close-after-success')
      }
    },
    onCancel() {
      this.$emit('update:externalOpen', false)
      this.$emit('cancel')
    },
    updateShowModal(value) {
      this.$emit('update:externalOpen', value)
      if (!value) {
        this.resetForm()
      }
    },
  }
}
</script>

<style scoped>
.merge-summary {
  margin: 20px 0;
}

.merge-schools-processing,
.merge-schools-success,
.merge-schools-error {
  padding: 20px 0;
}
</style>
